import { lazy } from 'react';

import { LayoutType, PageType } from '@shared/constants/constants';
import {
  ACTION_BACK_PATH,
  MEDREFILL_PUBLIC_INSTITUTION_PATH,
  MEDREFILL_PUBLIC_ORDER_TYPE_PATH,
  MEDREFILL_PUBLIC_PATH,
  MEDREFILL_PUBLIC_POLYCLINICS_PATH,
  MEDREFILL_PUBLIC_REFILL_ADD_PARTICULARS_PATH,
  MEDREFILL_PUBLIC_REFILL_COLLECTIONS_PATH,
  MEDREFILL_PUBLIC_REFILL_ORIGINAL_DETAILS_PATH,
  MEDREFILL_PUBLIC_REFILL_PAYMENT_MODE_PATH,
  MEDREFILL_PUBLIC_REFILL_PRESCRIPTION_LIST_PATH,
  MEDREFILL_PUBLIC_REFILL_REFILL_OPTIONS_PATH,
  MEDREFILL_PUBLIC_REFILL_SUCCESSFUL_REQUEST_PATH,
  MEDREFILL_PUBLIC_REFILL_SUMMARY_PATH,
  MEDREFILL_PUBLIC_REFILL_TNC_PATH,
  MEDREFILL_PUBLIC_REFILL_UNSUCCESSFUL_REQUEST_PATH,
  MEDREFILL_PUBLIC_SUCCESSFUL_REQUEST_PATH,
  MEDREFILL_PUBLIC_TOPUP_ADD_PARTICULARS_PATH,
  MEDREFILL_PUBLIC_TOPUP_CLINICS_PATH,
  MEDREFILL_PUBLIC_TOPUP_COLLECTIONS_PATH,
  MEDREFILL_PUBLIC_TOPUP_PAYMENT_MODE_PATH,
  MEDREFILL_PUBLIC_TOPUP_REPEAT_PRESCRIPTION_LIST_PATH,
  MEDREFILL_PUBLIC_TOPUP_SUCCESSFUL_REQUEST_PATH,
  MEDREFILL_PUBLIC_TOPUP_SUMMARY_PATH,
  MEDREFILL_PUBLIC_TOPUP_TNC_PATH,
  MEDREFILL_PUBLIC_TOPUP_UNSUCCESSFUL_REQUEST_PATH,
} from '@shared/constants/paths';
import { SERVICE_CATALOG_CODE } from '@shared/constants/serviceCatalog';
import type { RouteConfig } from '@shared/types/common.types';

const AddParticulars = lazy(() => import('./pages/AddParticulars/AddParticulars'));
const Collections = lazy(() => import('./pages/Collections/Collections'));
const MedRefillSummary = lazy(() => import('./pages/MedRefillSummary/MedRefillSummary'));
const Prescriptions = lazy(() => import('./pages/Prescriptions/Prescriptions'));
const RefillOptions = lazy(() => import('./pages/RefillOptions/RefillOptions'));
const SelectPaymentMode = lazy(() => import('./pages/SelectPaymentMode/SelectPaymentMode'));
const SuccessfulMedRequest = lazy(() => import('./pages/SuccessfulMedRequest/SuccessfulMedRequest'));
const UnsuccessfulMedRequest = lazy(() => import('./pages/UnsuccessfulMedRequest/UnsuccessfulMedRequest'));
const SelectPolyclinic = lazy(() => import('./pages/SelectPolyclinic/SelectPolyclinic'));
const SelectMedicationOrderType = lazy(() => import('./pages/SelectMedicationOrderType/SelectMedicationOrderType'));
const OriginalDetails = lazy(() => import('./pages/OriginalDetails/OriginalDetails'));
const Clinic = lazy(() => import('./pages/Clinic/Clinic'));
const SelectInstitution = lazy(() => import('./pages/SelectInstitution/SelectInstitution'));
const PublicLanding = lazy(() => import('./pages/PublicLanding/PublicLanding'));
const RepeatPrescriptions = lazy(() => import('./pages/RepeatPrescriptions/RepeatPrescriptions'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions/TermsAndConditions'));

export const MEDREFILL_PUBLIC_ROUTES: RouteConfig[] = [
  {
    actionUrl: ACTION_BACK_PATH,
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <PublicLanding />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MEDREFILL_PUBLIC_PATH,
    component: () => <SelectInstitution />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_INSTITUTION_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MEDREFILL_PUBLIC_INSTITUTION_PATH,
    component: () => <SelectPolyclinic />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_POLYCLINICS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectMedicationOrderType />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_ORDER_TYPE_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <OriginalDetails />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_REFILL_ORIGINAL_DETAILS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Clinic isTopup />,
    displayTitle: () => 'Repeat Prescription',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_TOPUP_CLINICS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Prescriptions />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_REFILL_PRESCRIPTION_LIST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <RefillOptions />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_REFILL_REFILL_OPTIONS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <AddParticulars />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_REFILL_ADD_PARTICULARS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <AddParticulars isTopup />,
    displayTitle: () => 'Repeat Prescription',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_TOPUP_ADD_PARTICULARS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <MedRefillSummary isTopup />,
    displayTitle: () => 'Repeat Prescription',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_TOPUP_SUMMARY_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <MedRefillSummary />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_REFILL_SUMMARY_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Collections isTopup />,
    displayTitle: () => 'Repeat Prescription',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_TOPUP_COLLECTIONS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Collections />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_REFILL_COLLECTIONS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectPaymentMode />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_REFILL_PAYMENT_MODE_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectPaymentMode isTopup />,
    displayTitle: () => 'Repeat Prescription',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_TOPUP_PAYMENT_MODE_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SuccessfulMedRequest />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_SUCCESSFUL_REQUEST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
    backUrl: MEDREFILL_PUBLIC_INSTITUTION_PATH,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SuccessfulMedRequest isTopup />,
    displayTitle: () => 'Repeat Prescription',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_TOPUP_SUCCESSFUL_REQUEST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
    backUrl: MEDREFILL_PUBLIC_INSTITUTION_PATH,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SuccessfulMedRequest />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_REFILL_SUCCESSFUL_REQUEST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
    backUrl: MEDREFILL_PUBLIC_INSTITUTION_PATH,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <UnsuccessfulMedRequest />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_REFILL_UNSUCCESSFUL_REQUEST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
    backUrl: MEDREFILL_PUBLIC_INSTITUTION_PATH,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <UnsuccessfulMedRequest isTopup />,
    displayTitle: () => 'Repeat Prescription',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_TOPUP_UNSUCCESSFUL_REQUEST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
    backUrl: MEDREFILL_PUBLIC_INSTITUTION_PATH,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <RepeatPrescriptions isTopup />,
    displayTitle: () => 'Repeat Prescription',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_TOPUP_REPEAT_PRESCRIPTION_LIST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <TermsAndConditions />,
    displayTitle: () => 'Medication Refill',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_REFILL_TNC_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <TermsAndConditions isTopup />,
    displayTitle: () => 'Repeat Prescription',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: MEDREFILL_PUBLIC_TOPUP_TNC_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicMedicationRefill,
  },
];
